import { cardPeriodForDate, isMovementInCurrentPeriod } from "./finance";
import type { CardConfig, FinancialSnapshot, Movement } from "./types";
import { isRecordedActivity, movementActivityDate } from "./bank-posting";

function activityInFortnight(snapshot: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">, movement: Movement): boolean {
  if ((movement.bankPostingState === "confirmed" || movement.bankPostingState === "scheduled") && snapshot.periodStart && snapshot.periodEnd) {
    const date = movementActivityDate(movement);
    return date >= snapshot.periodStart && date <= snapshot.periodEnd;
  }
  return isMovementInCurrentPeriod(snapshot, movement);
}

/** Vista de actividad: cortes de tarjeta independientes del presupuesto quincenal. */
export function movementsForCurrentPeriods(
  movements: Movement[],
  cards: Pick<CardConfig, "paymentMethod" | "cutDay">[],
  snapshot: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">,
  today: string,
): Movement[] {
  return movements.filter((movement) => {
    if (!isRecordedActivity(movement, today)) return false;
    const card = cards.find((candidate) => candidate.paymentMethod === movement.paymentMethod);
    if (!card) return activityInFortnight(snapshot, movement);
    const currentPeriod = cardPeriodForDate(today, card.cutDay);
    const storedPeriod = movement.cardPeriod
      ?? cardPeriodForDate(String(movement.spentOn ?? movement.createdAt).slice(0, 10), card.cutDay);
    return Boolean(currentPeriod && storedPeriod === currentPeriod);
  });
}

export interface MovementPeriodOption {
  id: string;
  label: string;
  detail: string;
  scope: "combined" | "fortnight" | "card";
  period?: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">;
  paymentMethod?: string;
  cardPeriod?: string;
  cutDay?: number;
  unavailable?: boolean;
}

/** El periodo anterior es el inmediatamente cerrado, aunque no tenga gastos. */
export function movementPeriodOptions(
  cards: Pick<CardConfig, "name" | "paymentMethod" | "cutDay">[],
  snapshot: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">,
  history: { period: string; start?: string; end?: string }[],
  today: string,
): MovementPeriodOption[] {
  const previous = history.find((item) => item.start && item.end
    && snapshot.periodStart && nextDay(item.end) === snapshot.periodStart);
  const options: MovementPeriodOption[] = [
    { id: "combined", label: "Cortes vigentes y quincena actual", detail: "Tarjetas: corte vigente · Otros medios: quincena actual", scope: "combined" },
    { id: "fortnight-current", label: "Esta quincena", detail: periodDescription(snapshot), scope: "fortnight", period: snapshot },
    { id: "fortnight-previous", label: "Quincena pasada", detail: previous ? periodDescription({ periodLabel: previous.period, periodStart: previous.start, periodEnd: previous.end }) : "La quincena anterior no está disponible en el historial cargado.", scope: "fortnight", period: previous ? { periodLabel: previous.period, periodStart: previous.start, periodEnd: previous.end } : undefined, unavailable: !previous },
  ];
  for (const card of cards) {
    const current = cardPeriodForDate(today, card.cutDay);
    const previousPeriod = current ? cardPeriodForDate(previousDay(current.slice(0, 10)), card.cutDay) : undefined;
    for (const [suffix, label, period] of [["current", "Corte actual", current], ["previous", "Corte pasado", previousPeriod]] as const) {
      options.push({ id: `card-${encodeURIComponent(card.paymentMethod)}-${suffix}`, label: `${card.name} · ${label}`, detail: period ? `${card.name} · ${period}` : "El corte de esta tarjeta no está disponible.", scope: "card", paymentMethod: card.paymentMethod, cutDay: card.cutDay, cardPeriod: period, unavailable: !period });
    }
  }
  return options;
}

function shiftDay(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
const previousDay = (value: string) => shiftDay(value, -1);
const nextDay = (value: string) => shiftDay(value, 1);
function periodDescription(period: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">): string {
  return [period.periodLabel, period.periodStart && period.periodEnd ? `${period.periodStart} al ${period.periodEnd}` : ""].filter(Boolean).join(" · ");
}

export function movementsForPeriod(
  movements: Movement[],
  option: MovementPeriodOption,
  cards: Pick<CardConfig, "paymentMethod" | "cutDay">[],
  snapshot: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">,
  today: string,
): Movement[] {
  if (option.unavailable) return [];
  if (option.scope === "combined") return movementsForCurrentPeriods(movements, cards, snapshot, today);
  return movements.filter((movement) => {
    if (!isRecordedActivity(movement, today)) return false;
    if (option.scope === "fortnight") return Boolean(option.period && activityInFortnight(option.period, movement));
    if (movement.paymentMethod !== option.paymentMethod || !option.cardPeriod || !option.cutDay) return false;
    const period = movement.cardPeriod ?? cardPeriodForDate(String(movement.spentOn ?? movement.createdAt).slice(0, 10), option.cutDay);
    return period === option.cardPeriod;
  });
}
